import { API_CALL, OFFER_CALENDAR_RECOMMENDATIONS_CLEAR, REMOVE_DISMISSED_ABANDONED_CART_OFFER_IDS, SET_LERE_VERSION } from 'actions/actionConstants'
import {
  CACHE_LERE_RECOMMENDATIONS,
  FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
  FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS,
  FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
  FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
  FETCH_LERE_PERSONALISED_ABANDONED_CARTS,
  DISMISS_LERE_PERSONALISED_ABANDONED_CARTS,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
  FETCH_LERE_PERSONALISED_CONTEXT,
  FETCH_LERE_TOP_DEALS,
  FETCH_LERE_HERO_PLANNER,
  FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS,
  FETCH_LERE_HOT_LEADS,
  SAVE_LERE_HOT_LEADS,
} from 'actions/apiActionConstants'
import * as RecommendationService from 'api/recommendations'
import { fetchOfferById, fetchOffersById } from 'actions/OfferActions'
import { getLereChallengerEnabledVariant, getLerePersonalisedAbandonedCartsExperimentVariant } from 'selectors/featuresSelectors'
import { lereLocationSeededCacheKey, lereLocationFlexCacheKey, lerePreCacheKey, waitOnCondition, lereDlpParameters } from 'lib/recommendationUtils'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import config from 'constants/config'
import { Filters } from 'api/recommendations'
import { reportUserTimingEvent } from 'lib/performance'
import noop from 'lib/noop'
import { AbandonedCartViewModel } from 'selectors/recommendations'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { fetchBedbankOffersById } from './BedbankOfferActions'
import { parseSearchString } from 'lib/urlUtils'

/**
 *
 * @param offerId offer id
 * @param mergeResults sometimes, you do not want to remove the existing state (that could cause the LERE ui touchpoint to re-render, for example). Then, use mergeResults - it will append new offer ids to the end.
 * @param checkIn Check in date in ISO format
 * @param checkOut Check out date in ISO format
 * @returns
 */
export function fetchOfferBasedRecommendations(offerId: string, mergeResults: boolean, checkIn?: string, checkOut?: string) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const cacheKey = offerId

    let checkInParam = undefined
    let checkOutParam = undefined
    if (checkIn && checkOut) {
      checkInParam = checkIn
      checkOutParam = checkOut
    }

    const filterKey = checkInParam && checkOutParam ? [checkIn, checkOut].join(';') : '-'

    const recommendationsState = state.recommendations.recommendations[cacheKey]

    if (['loading', 'done'].includes(recommendationsState?.state)) {
      if (mergeResults) {
        if (recommendationsState?.filtersFetched.includes(filterKey)) {
          return
        }
      } else {
        return
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
      request: async() => {
        const attemptsLeft = await waitOnCondition(() => config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID && getLereChallengerEnabledVariant(getState()) === 'pending')
        const variant = getLereChallengerEnabledVariant(getState())
        const challengerEnabled = variant === 'enabled'

        fireNonInteractionEvent({
          category: 'recommendations',
          action: `challenger-recommendation-experiment-${variant}`,
          label: `attemptsLeft-${attemptsLeft}`,
        })
        const result = await RecommendationService.getRecommendations(offerId, state.geo.currentRegionCode, challengerEnabled, checkInParam, checkOutParam)
        const { recommendations, lereVersion } = result
        dispatch({ type: SET_LERE_VERSION, lereVersion })

        const recommendationOfferIds = recommendations.map((r) => r.offerId)
        dispatch(fetchOffersById(recommendationOfferIds, { includePackages: true }))
        return recommendationOfferIds
      },
      cacheKey,
      filterKey,
      mergeResults,
    })
  }
}

let _requestId = 1

export function fetchCalendarRecommendations(offerId: string, filters: {
  checkIn?: string;
  availableMonth: string;
  rooms?: App.Occupants[];
  duration?: number;
}) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    _requestId++

    const cacheKey = lerePreCacheKey(offerId, state.geo.currentRegionCode, { duration: filters.duration, rooms: filters.rooms })

    const cache = getState().recommendations.lerePrecached[cacheKey]
    if (!['done', 'error'].includes(cache?.state)) {
      const duration = cache ? (Date.now() - cache.startTime) : -1
      reportUserTimingEvent({
        eventCategory: 'recommendations',
        variable: 'fetchCalendarCacheNotFinished',
        value: duration,
        label: `notFinished_${cache?.state}`,
      })
    }

    dispatch({
      type: API_CALL,
      api: FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
      request: async() => {
        const retriesLeft = await waitOnCondition(() => config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID && getLereChallengerEnabledVariant(getState()) === 'pending')
        const variant = getLereChallengerEnabledVariant(getState())
        const challengerEnabled = variant === 'enabled'

        fireNonInteractionEvent({
          category: 'recommendations',
          action: `challenger-recommendation-experiment-${variant}`,
          label: `retriesLeft-${retriesLeft}`,
        })

        const params = {
          checkIn: filters.checkIn,
          availableMonth: filters.availableMonth,
          rooms: filters.rooms,
          duration: filters.duration,
        }

        const { recommendations, lereVersion } = await RecommendationService.getCalendarRecommendations(offerId, state.geo.currentRegionCode, params, challengerEnabled)
        const recommendationOfferIds = recommendations.map((r) => r.offerId)
        dispatch({ type: SET_LERE_VERSION, lereVersion })
        dispatch(fetchOffersById(recommendationOfferIds, { includePackages: true }))
        return {
          offerIds: recommendationOfferIds,
          requestId: _requestId,
        }
      },
      offerId,
      requestId: _requestId,
    })
  }
}

export function cacheLereRecommendations(offerId: string, filters: Pick<Filters, 'duration' | 'rooms'>) {
  return async(dispatch, getState: () => App.State) => {
    const state = getState()
    const cacheKey = lerePreCacheKey(offerId, state.geo.currentRegionCode, {
      duration: filters.duration,
      rooms: filters.rooms,
    })

    if (getState().recommendations.lerePrecached[cacheKey]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: CACHE_LERE_RECOMMENDATIONS,
      request: async() => {
        return RecommendationService.cacheRecommendations(offerId, state.geo.currentRegionCode, filters, false).catch(noop)
      },
      cacheKey,
    })
  }
}

export function clearCalendarOfferRecommendations() {
  return {
    type: OFFER_CALENDAR_RECOMMENDATIONS_CLEAR,
  }
}

interface FetchLocationSeededRecommendationsParams {
  destinationId: string
  polygonMode: RecommendationService.PolygonMode
  nearDestinations: boolean
  useCacheIfExists?: boolean
  checkIn?: string
  checkOut?: string
  duration?: number
  useCentroid?: boolean
}

export function fetchLocationSeededRecommendations({
  destinationId,
  polygonMode,
  nearDestinations,
  useCacheIfExists = false,
  checkIn,
  checkOut,
  duration,
  useCentroid = true,
}: FetchLocationSeededRecommendationsParams) {
  return async(dispatch, getState: () => App.State) => {
    const destinationKey = lereLocationSeededCacheKey({
      destinationId,
      polygonMode,
      nearDestinations,
      checkIn,
      checkOut,
      duration,
    })

    if (useCacheIfExists) {
      const existingRecommendations = getState().recommendations.locationSeededRecommendations[destinationKey]
      if (existingRecommendations && ['loading', 'done'].includes(existingRecommendations.state)) {
        return
      }
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_LOCATION_SEEDED_RECOMMENDATIONS,
      request: async() => {
        const attemptsLeft = await waitOnCondition(() => config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID && getLereChallengerEnabledVariant(getState()) === 'pending', 250, 2)
        const variant = getLereChallengerEnabledVariant(getState())
        const challengerEnabled = variant === 'enabled'

        fireNonInteractionEvent({
          category: 'recommendations',
          action: `challenger-location-seeded-recommendation-experiment-${variant}`,
          label: `attemptsLeft-${attemptsLeft}`,
        })

        let checkInParam = undefined
        let checkOutParam = undefined
        let durationParam = undefined
        if (checkIn && checkOut) {
          checkInParam = checkIn
          checkOutParam = checkOut
        }
        if (checkIn && duration) {
          checkInParam = checkIn
          durationParam = duration
        }
        const { recommendations, lereVersion } = await RecommendationService.getLocationSeededRecommendations({
          destinationId,
          polygonMode,
          nearDestination: nearDestinations,
          challengerEnabled,
          checkInDate: checkInParam,
          checkOutDate: checkOutParam,
          duration: durationParam,
          useCentroid,
        })
        dispatch({ type: SET_LERE_VERSION, lereVersion })

        const recommendationOfferIds = recommendations.map((r) => r.offerId)
        dispatch(fetchOffersById(recommendationOfferIds, { includePackages: true }))
        return recommendationOfferIds
      },
      destinationKey,
    })
  }
}

export function fetchLocationFlexRecommendations(params: RecommendationService.GetLocationFlexRecommendations) {
  const cacheKey = lereLocationFlexCacheKey(params)

  return async(dispatch, getState: () => App.State) => {
    dispatch({
      type: API_CALL,
      api: FETCH_LERE_LOCATION_FLEX_RECOMMENDATIONS,
      request: async() => {
        const attemptsLeft = await waitOnCondition(() => config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID && getLereChallengerEnabledVariant(getState()) === 'pending', 250, 2)
        const variant = getLereChallengerEnabledVariant(getState())

        fireNonInteractionEvent({
          category: 'recommendations',
          action: `challenger-location-seeded-recommendation-experiment-${variant}`,
          label: `attemptsLeft-${attemptsLeft}`,
        })

        const { recommendations, lereVersion } = await RecommendationService.getLocationFlexRecommendations(params)
        dispatch({ type: SET_LERE_VERSION, lereVersion })

        const recommendationOfferIds = recommendations.map((r) => r.offerId)

        dispatch(fetchOffersById(recommendationOfferIds, { includePackages: true }))

        return recommendations
      },
      cacheKey,
    })
  }
}

// #region DLP - Destination Landing Page

export function fetchDlpMainRecommendations(destinationId: string) {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.mainList.polygonMode,
    nearDestinations: lereDlpParameters.mainList.nearDestinations,
  })
}

export function fetchDlpYmalRecommendations(destinationId: string) {
  return fetchLocationSeededRecommendations({
    destinationId,
    polygonMode: lereDlpParameters.ymal.polygonMode,
    nearDestinations: lereDlpParameters.ymal.nearDestinations,
  })
}

// #endregion

async function waitForPersonalisedAcVariant(appState: App.State) {
  await waitOnCondition(
    () => {
      const variant = getLerePersonalisedAbandonedCartsExperimentVariant(appState)
      return variant !== 'disabled' && variant !== 'unavailable'
    }
    ,
    250,
    5,
  )
}

export function fetchPersonalisedAbandonedCarts() {
  return async(dispatch, getState: () => App.State) => {
    if (!config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID) {
      return
    }

    const appState = getState()
    const memberId = appState.auth?.account?.memberId || getQueryParamUserId(appState)
    if (!memberId) {
      return
    }
    await waitForPersonalisedAcVariant(getState())
    const variant = getLerePersonalisedAbandonedCartsExperimentVariant(getState())

    if (variant === 'disabled' || variant === 'unavailable') {
      return
    }

    const region = appState.geo.currentRegionCode

    function fetchCartOfferDetails(carts: Array<App.PersonalisedAbandonedCarts>) {
      const cartItemsCollected = (carts || []).reduce<{ nonLpp: App.PersonalisedAbandonedCart[], lpp: App.PersonalisedAbandonedCart[] }>((collector, cart) => {
        // We only work with single cart items
        const cartItem = cart.cartItems[0]
        if (cartItem.offerType === OFFER_TYPE_BED_BANK) {
          collector.lpp.push(cartItem)
        } else {
          collector.nonLpp.push(cartItem)
        }
        return collector
      }, { nonLpp: [], lpp: [] })

      const nonLppOfferIds = cartItemsCollected.nonLpp.map((nonLppCartItem) => {
        return nonLppCartItem.offerId
      })
      dispatch(fetchOffersById(nonLppOfferIds, { includePackages: true }))

      const lppOfferIds = cartItemsCollected.lpp.map((lppCartItem) => {
        return lppCartItem.offerId
      })
      dispatch(fetchBedbankOffersById(lppOfferIds))
    }

    const personalisedAbandonedCartsState = getState().recommendations.personalisedAbandonedCarts[variant]

    if (personalisedAbandonedCartsState.state === 'done') {
      fetchCartOfferDetails(personalisedAbandonedCartsState.carts)
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_ABANDONED_CARTS,
      request: async() => {
        const { carts, lereVersion } = await RecommendationService.getPersonalisedAbandonedCarts(memberId, variant, region)

        fetchCartOfferDetails(carts)

        return { carts, lereVersion }
      },
      variant,
    })
  }
}

export function dismissPersonalisedAbandonedCarts(cart: AbandonedCartViewModel) {
  return async(dispatch, getState: () => App.State) => {
    if (!config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID) {
      return
    }

    const appState = getState()
    const memberId = appState.auth?.account?.memberId || getQueryParamUserId(appState)
    if (!memberId) {
      return
    }

    await waitForPersonalisedAcVariant(getState())
    const variant = getLerePersonalisedAbandonedCartsExperimentVariant(getState())

    if (variant === 'disabled' || variant === 'unavailable') {
      return
    }

    dispatch({
      type: API_CALL,
      api: DISMISS_LERE_PERSONALISED_ABANDONED_CARTS,
      request: async() => {
        await RecommendationService.putDismissPersonalisedAbandonedCarts(memberId, variant, cart.offer?.id)
      },
      variant,
      cart,
    })
  }
}

export function getQueryParamUserId(state: App.State) {
  const params = parseSearchString(state.router.location.search)
  return params.user_id
}

function splitOfferIds(offerIds: string[]) {
  const uuids = []
  const leOfferIds = []
  offerIds.forEach(offerId => {
    // We want to ignore bedbank, but this might ignore other types, like tour v2s, as well.
    // Might revisit later.
    if (offerId.length >= 36) {
      uuids.push(offerId)
    } else {
      leOfferIds.push(offerId)
    }
  })
  return { bedbankOfferIds: uuids, leOfferIds }
}

export function fetchPeopleLikeMeRecommendations() {
  return async(dispatch, getState: () => App.State) => {
    const appState = getState()
    const memberId = appState.auth?.account?.memberId || getQueryParamUserId(appState)
    if (!memberId) {
      return
    }
    const personalisedPeopleLikeMeState = appState.recommendations.personalisedPeopleLikeMe
    const region = appState.geo.currentRegionCode

    if (personalisedPeopleLikeMeState.state === 'done') {
      dispatch(fetchOffersById(personalisedPeopleLikeMeState.offerIds, { includePackages: true }))
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
      request: async() => {
        const { offerIds, lereVersion } = await RecommendationService.getPeopleLikeMeRecommendations(memberId, region)
        dispatch(fetchOffersById(offerIds, { includePackages: true }))

        return { offerIds, lereVersion }
      },
    })
  }
}

export function fetchPersonalisedContext() {
  return async(dispatch, getState: () => App.State) => {
    const appState = getState()
    const memberId = appState.auth?.account?.memberId || getQueryParamUserId(appState)
    if (!memberId) {
      return
    }
    const personalisedContextState = getState().recommendations.personalisedContext

    if (personalisedContextState.state === 'done') {
      dispatch(fetchOffersById(personalisedContextState.offerIds, { includePackages: true }))
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_CONTEXT,
      request: async() => {
        const { offerIds, lereVersion } = await RecommendationService.getPersonalisedContextViewHistory(memberId)

        const { leOfferIds } = splitOfferIds(offerIds)

        dispatch(fetchOffersById(leOfferIds, { includePackages: true }))

        return { offerIds, lereVersion }
      },
    })
  }
}

export function fetchPersonalisedPeopleLikeMeExists() {
  return async(dispatch, getState: () => App.State) => {
    const appState = getState()
    const memberId = appState.auth?.account?.memberId || getQueryParamUserId(appState)
    if (!memberId) {
      return
    }
    const personalisationPeopleLikeMeExists = getState().recommendations.personalisationPeopleLikeMeExists

    if (personalisationPeopleLikeMeExists.state === 'done') {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME_EXISTS,
      request: async() => {
        const region = getState().geo.currentRegionCode

        const { exists, lereVersion } = await RecommendationService.getPersonalisationExistsForUser(memberId, region)
        return { exists, lereVersion }
      },
    })
  }
}

export function fetchTopDeals() {
  return async(dispatch, getState: () => App.State) => {
    const topDeals = getState().recommendations.topDeals

    if (topDeals.state === 'done') {
      dispatch(fetchOffersById(topDeals.offerIds, { includePackages: true }))
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOP_DEALS,
      request: async() => {
        const { offers, lereVersion } = await RecommendationService.getTopDeals(getState().geo.currentRegionCode)

        const leOfferIds = offers.filter(o => [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE]
          .includes(o.offer_type))
          .map(o => o.offer_id)

        dispatch(fetchOffersById(leOfferIds, { includePackages: true }))

        return { offerIds: leOfferIds, lereVersion }
      },
    })
  }
}

export function fetchHeroPlanner() {
  return async(dispatch, getState: () => App.State) => {
    const heroPlanner = getState().recommendations.heroPlanner

    if (heroPlanner.state === 'done') {
      dispatch(fetchOffersById(heroPlanner.offerIds, { includePackages: true }))
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HERO_PLANNER,
      request: async() => {
        const { offerIds, lereVersion } = await RecommendationService.getHeroPlanner(getState().geo.currentRegionCode)

        const { leOfferIds } = splitOfferIds(offerIds)

        dispatch(fetchOffersById(leOfferIds, { includePackages: true }))

        return { offerIds, lereVersion }
      },
    })
  }
}

export function fetchHotLead() {
  return async(dispatch, getState: () => App.State) => {
    const hotLead = getState().recommendations.hotLead
    const userId = getState().auth.account.memberId

    if (!userId || hotLead.state === 'done') return

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HOT_LEADS,
      request: async() => {
        const { hotLeadData, lereVersion } = await RecommendationService.getHotLead(userId)

        if (hotLeadData.offerId) {
          dispatch(fetchOfferById(hotLeadData.offerId, { allPackages: true }))
        }

        return { hotLeadData, lereVersion }
      },
    })
  }
}

export function saveHotLead(hotLeadData: App.OfferHotLead) {
  return (dispatch, getState: () => App.State) => {
    const userId = getState().auth.account.memberId

    // we always save the state locally
    // only call remote save if user logged in
    dispatch({
      type: SAVE_LERE_HOT_LEADS,
      hotLeadData,
    })

    if (userId) {
      // don't need to await for the response
      RecommendationService.saveHotLead(userId, hotLeadData)
    }
  }
}

export function dismissHotLead() {
  return async(dispatch, getState: () => App.State) => {
    const hotLeadData = {
      ...getState().recommendations.hotLead.data,
      isDismissed: true,
    }
    dispatch(saveHotLead(hotLeadData))
  }
}

export function clearHotLead() {
  return saveHotLead({ offerId: null, occupancy: null, isDismissed: false })
}

export function removeDismissedAbandonedCartOfferIds(offerIds: Array<string>) {
  return {
    type: REMOVE_DISMISSED_ABANDONED_CART_OFFER_IDS,
    offerIds,
  }
}
